import { createContext, useContext, useCallback, useRef } from 'react';

const SearchContext = createContext(null);

export const SearchProvider = ({ children }) => {
  // Registry of search input refs by page/context
  const searchInputRefs = useRef({});
  
  // Register a search input for a specific context
  const registerSearchInput = useCallback((contextId, ref) => {
    if (ref) {
      searchInputRefs.current[contextId] = ref;
    }
  }, []);
  
  // Unregister a search input when component unmounts
  const unregisterSearchInput = useCallback((contextId) => {
    delete searchInputRefs.current[contextId];
  }, []);
  
  // Focus the search input for a specific context
  const focusSearchInput = useCallback((contextId) => {
    const ref = searchInputRefs.current[contextId];
    if (ref && ref.current) {
      // Scroll into view if needed
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the input after a small delay for smooth scroll
      setTimeout(() => {
        ref.current.focus();
      }, 100);
      return true;
    }
    return false;
  }, []);
  
  // Check if a context has a registered search input
  const hasSearchInput = useCallback((contextId) => {
    const ref = searchInputRefs.current[contextId];
    return !!ref && !!ref.current;
  }, []);
  
  // Get all registered contexts
  const getRegisteredContexts = useCallback(() => {
    return Object.keys(searchInputRefs.current);
  }, []);
  
  return (
    <SearchContext.Provider value={{
      registerSearchInput,
      unregisterSearchInput,
      focusSearchInput,
      hasSearchInput,
      getRegisteredContexts
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Hook for components to register their search input
export const useSearchInput = (contextId) => {
  const { registerSearchInput, unregisterSearchInput } = useSearch();
  const inputRef = useRef(null);
  
  // Register on mount, unregister on unmount
  const setRef = useCallback((node) => {
    inputRef.current = node;
    if (node) {
      registerSearchInput(contextId, inputRef);
    }
  }, [contextId, registerSearchInput]);
  
  // Cleanup on unmount
  const cleanup = useCallback(() => {
    unregisterSearchInput(contextId);
  }, [contextId, unregisterSearchInput]);
  
  return { inputRef: setRef, cleanup };
};

export default SearchContext;
